import {
  DocumentSyncBackend,
  emptySyncDocument,
  isSyncDocument,
  type SyncBackend,
  type SyncDocument,
  type SyncManifest,
  type SyncRecord,
} from "@roqeet/core";

/**
 * Google Drive 동기화 백엔드 — 결정 #20(+#8). 사용자의 **본인 Drive appDataFolder**에
 * 단일 파일(`roqeet-sync.json`)로 {@link SyncDocument}를 싣는다. 우리 서버는 데이터를
 * 보지 않는다(결정 #8). 병합 로직은 이미 검증된 {@link DocumentSyncBackend}에 위임하고,
 * 이 클래스는 **전송(다운로드/업로드)만** 담당한다.
 *
 * 수명주기(파일 동기화와 동일한 패턴):
 *  1) `load()`  — Drive에서 기존 문서를 받아 내부 DocumentSyncBackend를 초기화.
 *  2) `runSync(local, this, onConflict)` — pull/merge/push가 인메모리 문서에 반영.
 *  3) `flush()` — 병합된 문서를 Drive 파일에 다시 업로드(없으면 생성).
 *
 * Drive REST 호출은 {@link DriveFileClient}로 추상화해 테스트(가짜 클라이언트)와
 * 실제 구현(GoogleDriveClient)을 분리한다(결정 #10 정신).
 */

/** appDataFolder에 둘 동기화 파일 이름. */
export const DRIVE_SYNC_FILENAME = "roqeet-sync.json";

/** Drive 파일 입출력 최소 인터페이스(테스트 가능 경계). */
export interface DriveFileClient {
  /** appDataFolder에서 이름으로 파일 id를 찾는다(없으면 undefined). */
  findFile(name: string): Promise<string | undefined>;
  /** 파일 본문(텍스트)을 받는다. */
  downloadFile(fileId: string): Promise<string>;
  /** appDataFolder에 새 파일을 만들고 id를 반환한다. */
  createFile(name: string, content: string): Promise<string>;
  /** 기존 파일 본문을 갱신한다. */
  updateFile(fileId: string, content: string): Promise<void>;
}

export class GoogleDriveSyncBackend implements SyncBackend {
  readonly kind = "google-drive";
  private readonly client: DriveFileClient;
  private inner: DocumentSyncBackend = new DocumentSyncBackend();
  private fileId: string | undefined;
  private loaded = false;

  constructor(client: DriveFileClient) {
    this.client = client;
  }

  /** Drive에서 기존 문서를 받아 내부 백엔드를 초기화한다(없으면 빈 문서). */
  async load(): Promise<void> {
    const id = await this.client.findFile(DRIVE_SYNC_FILENAME);
    if (id) {
      this.fileId = id;
      const text = await this.client.downloadFile(id);
      let doc: SyncDocument = emptySyncDocument();
      try {
        const parsed = JSON.parse(text);
        if (isSyncDocument(parsed)) doc = parsed;
      } catch {
        // 손상된 파일이면 빈 문서로 시작(다음 flush가 정상 문서로 덮어쓴다).
      }
      this.inner = new DocumentSyncBackend(doc);
    } else {
      this.inner = new DocumentSyncBackend();
    }
    this.loaded = true;
  }

  /** 병합된 문서를 Drive에 업로드한다(파일 없으면 생성). */
  async flush(): Promise<void> {
    if (!this.loaded) throw new Error("flush 전에 load()를 호출해야 합니다.");
    const content = JSON.stringify(this.inner.toDocument());
    if (this.fileId) {
      await this.client.updateFile(this.fileId, content);
    } else {
      this.fileId = await this.client.createFile(DRIVE_SYNC_FILENAME, content);
    }
  }

  // --- SyncBackend 위임(검증된 DocumentSyncBackend로) ---
  pullManifest(): Promise<SyncManifest> {
    return this.inner.pullManifest();
  }
  pullRecords(ids: string[]): Promise<SyncRecord[]> {
    return this.inner.pullRecords(ids);
  }
  pushRecords(records: SyncRecord[]): Promise<void> {
    return this.inner.pushRecords(records);
  }
  pullBlob(hash: string): Promise<Blob | undefined> {
    return this.inner.pullBlob(hash);
  }
  pushBlob(hash: string, blob: Blob): Promise<void> {
    return this.inner.pushBlob(hash, blob);
  }
  hasBlob(hash: string): Promise<boolean> {
    return this.inner.hasBlob(hash);
  }
}

/**
 * 실제 Google Drive REST 클라이언트(결정 #8). appDataFolder 범위만 쓴다 — 사용자의
 * 다른 파일에는 접근 권한이 없다. access token은 GoogleIdentityProvider가 공급한다.
 */
export class GoogleDriveClient implements DriveFileClient {
  constructor(private readonly accessToken: string) {}

  private auth(): Record<string, string> {
    return { authorization: `Bearer ${this.accessToken}` };
  }

  async findFile(name: string): Promise<string | undefined> {
    const q = encodeURIComponent(`name='${name.replace(/'/g, "\\'")}'`);
    const url =
      `https://www.googleapis.com/drive/v3/files?spaces=appDataFolder&fields=files(id,name)&q=${q}`;
    const res = await fetch(url, { headers: this.auth() });
    if (!res.ok) throw new Error(`Drive 파일 조회 실패(${res.status}): ${await res.text()}`);
    const json = (await res.json()) as { files?: { id: string; name: string }[] };
    return json.files?.[0]?.id;
  }

  async downloadFile(fileId: string): Promise<string> {
    const url = `https://www.googleapis.com/drive/v3/files/${fileId}?alt=media`;
    const res = await fetch(url, { headers: this.auth() });
    if (!res.ok) throw new Error(`Drive 다운로드 실패(${res.status}): ${await res.text()}`);
    return res.text();
  }

  async createFile(name: string, content: string): Promise<string> {
    // multipart 업로드: 메타데이터(appDataFolder 부모) + 본문을 한 번에.
    const boundary = "roqeet-" + Math.random().toString(36).slice(2);
    const metadata = JSON.stringify({ name, parents: ["appDataFolder"] });
    const body =
      `--${boundary}\r\n` +
      "content-type: application/json; charset=UTF-8\r\n\r\n" +
      `${metadata}\r\n` +
      `--${boundary}\r\n` +
      "content-type: application/json\r\n\r\n" +
      `${content}\r\n` +
      `--${boundary}--`;
    const res = await fetch(
      "https://www.googleapis.com/upload/drive/v3/files?uploadType=multipart&fields=id",
      {
        method: "POST",
        headers: { ...this.auth(), "content-type": `multipart/related; boundary=${boundary}` },
        body,
      },
    );
    if (!res.ok) throw new Error(`Drive 생성 실패(${res.status}): ${await res.text()}`);
    const json = (await res.json()) as { id: string };
    return json.id;
  }

  async updateFile(fileId: string, content: string): Promise<void> {
    const res = await fetch(
      `https://www.googleapis.com/upload/drive/v3/files/${fileId}?uploadType=media`,
      {
        method: "PATCH",
        headers: { ...this.auth(), "content-type": "application/json" },
        body: content,
      },
    );
    if (!res.ok) throw new Error(`Drive 갱신 실패(${res.status}): ${await res.text()}`);
  }
}
